import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Table, Button } from "antd";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import SearchBox from "../../../components/SearchBox";
import SubscriberModelPopup from "./components/SubscriberModelPopup";
import SubscriberRemarksPopup from "./components/SubscriberRemarksPopup";
import { useNotification } from "../../context/NotificationContext";

const SubscriberList = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedSubscriberForRemarks, setSelectedSubscriberForRemarks] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(true);
  const maximizeBtnRef = useRef(null);
  const [isFullScreen, setFullScreen] = useState(false);
  const { notify } = useNotification();

  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        setFullScreen(true);
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
          setFullScreen(false);
        }
      }
    };

    const cleanup = () => {
      if (isFullScreen && document.exitFullscreen) {
        document.exitFullscreen();
        setFullScreen(false);
      }
    };

    const maximizeBtn = maximizeBtnRef.current;
    if (maximizeBtn) {
      maximizeBtn.addEventListener('click', handleClick);
    }

    return () => {
      if (maximizeBtn) {
        maximizeBtn.removeEventListener('click', handleClick);
      }
      cleanup();
    };
  }, [isFullScreen]);

  const fetchSubscribers = async () => {
    try {
      const response = await axios.get(`${apiUrl}/campaign/contacts`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setSubscribers(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching subscribers:", error);
      notify("error", "Failed to fetch subscribers. Please try again later.");
      setLoading(false);
    }
  };

  useEffect(() => {
    if (apiUrl && token) {
      fetchSubscribers();
    }
  }, [apiUrl, token]);

  const handleSave = (subscriber) => {
    if (isEditing) {
      setSubscribers((prevSubscribers) =>
        prevSubscribers.map((d) => (d.id === subscriber.id ? subscriber : d))
      );
    } else {
      setSubscribers((prevSubscribers) => [...prevSubscribers, subscriber]);
    }
    setIsEditing(false);
    setSelectedSubscriber(null);
  };

  const handleEditClick = (subscriber) => {
    setSelectedSubscriber(subscriber);
    setIsEditing(true);
  };

  const handleAddClick = () => {
    setIsEditing(false);
    setSelectedSubscriber(null);
  };

  const handleRemarksClick = (subscriber) => {
    setSelectedSubscriberForRemarks(subscriber);
  };

  const handleDeleteClick = async (subscriberId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this subscriber!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      const subscriberToDelete = subscribers.find(s => s.id === subscriberId);
      setSubscribers((prevSubscribers) =>
        prevSubscribers.filter((subscriber) => subscriber.id !== subscriberId)
      );

      try {
        await axios.delete(`${apiUrl}/campaign/contacts/delete/${subscriberId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        notify("success", "Subscriber has been deleted.");
      } catch (error) {
        setSubscribers((prevSubscribers) => [...prevSubscribers, subscriberToDelete]);
        let errorMessage = "Failed to delete subscriber. Please try again later.";
        if (error.response && error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        }
        await Swal.fire("Error!", errorMessage, "error");
      }
    }
  };

  const updateSubscriberStatus = async (subscriberId, currentStatus) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to change the status to ${newStatus === 1 ? 'Active' : 'Inactive'}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, change it!',
      cancelButtonText: 'No, cancel!',
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      const previousStatus = currentStatus;
      setSubscribers(
        subscribers.map((subscriber) =>
          subscriber.id === subscriberId
            ? { ...subscriber, status: newStatus }
            : subscriber
        )
      );

      try {
        await axios.put(
          `${apiUrl}/campaign/contacts/update/${subscriberId}?isstatus=1`,
          { status: newStatus },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        notify("success", 'Subscriber status has been updated.');
      } catch (error) {
        setSubscribers(
          subscribers.map((subscriber) =>
            subscriber.id === subscriberId
              ? { ...subscriber, status: previousStatus }
              : subscriber
          )
        );
        let errorMessage = 'Failed to update subscriber status. Please try again later.';
        if (error.response && error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        }
        await Swal.fire('Error!', errorMessage, 'error');
      }
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      sorter: (a, b) => a.phone.localeCompare(b.phone),
    },
    {
      title: 'Industry',
      dataIndex: 'industry',
      key: 'industry',
      sorter: (a, b) => a.industry.localeCompare(b.industry),
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      sorter: (a, b) => a.country.localeCompare(b.country),
    },
    {
      title: 'Remarks',
      key: 'remarks',
      render: (text, record) => (record.Remarks ? record.Remarks.length : 0),
      sorter: (a, b) => (a.Remarks ? a.Remarks.length : 0) - (b.Remarks ? b.Remarks.length : 0),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text, record) => {
        const isActive = text === 1;
        const statusText = isActive ? 'Active' : 'Inactive';
        return (
          <div className="dropdown action-label">
            <Link
              className={`btn btn-white btn-sm btn-rounded dropdown-toggle ${isActive ? 'text-success' : 'text-danger'}`}
              to="#"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className={`far fa-dot-circle ${isActive ? 'text-success' : 'text-danger'}`} />{' '}
              {statusText}
            </Link>
            <div className="dropdown-menu">
              <Link
                className="dropdown-item"
                to="#"
                onClick={() => updateSubscriberStatus(record.id, record.status)}
              >
                <i className={`far fa-dot-circle ${!isActive ? 'text-success' : 'text-danger'}`} />{' '}
                {!isActive ? 'Active' : 'Inactive'}
              </Link>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <div className="dropdown dropdown-action text-center">
          <Link
            to="#"
            className="action-icon dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="material-icons">more_vert</i>
          </Link>
          <div className="dropdown-menu dropdown-menu-center">
            <Link
              onClick={() => handleEditClick(record)}
              className="dropdown-item"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#add_subscriber"
            >
              <i className="fa fa-pencil m-r-5" /> Edit
            </Link>
            <Link
              onClick={() => handleRemarksClick(record)}
              className="dropdown-item"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#manage_remarks"
            >
              <i className="fa fa-comment m-r-5" /> Manage Remarks
            </Link>
            <button
              className="dropdown-item"
              onClick={() => handleDeleteClick(record.id)}
            >
              <i className="fa fa-trash m-r-5" /> Delete
            </button>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="page-header page-top-icons">
            <div className="row align-items-center">
              <div className="col-md-4">
                <h3 className="page-title">Contacts</h3>
              </div>
              <div className="col-md-8 float-end ms-auto">
                <div className="d-flex title-head">
                  <div className="view-icons">
                    <Link to="#" className="grid-view btn btn-link" onClick={fetchSubscribers}><i className="las la-redo-alt" /></Link>
                    <Link to="#" className="list-view btn btn-link" id="collapse-header" ref={maximizeBtnRef}><i className="las la-expand-arrows-alt" /></Link>
                  </div>
                  <Link to="#" className="btn add-btn" data-bs-toggle="modal" data-bs-target="#add_subscriber" onClick={handleAddClick}>
                    <i className="la la-plus-circle" /> Add New
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              {loading ? (
                <div id="loader-wrapper-">
                  <div className="loader">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <SearchBox />
                  <Table
                    className="table-striped"
                    style={{ overflowX: "auto" }}
                    columns={columns}
                    dataSource={subscribers}
                    rowKey={(record) => record.id}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <SubscriberModelPopup onSave={handleSave} subscriberData={selectedSubscriber} isEditing={isEditing} />
      <SubscriberRemarksPopup subscriberData={selectedSubscriberForRemarks} />
    </>
  );
};

export default SubscriberList;